<script setup>
import {onBeforeMount, reactive, ref} from 'vue'
import {Feedback, State, clone} from '@/paks/vu-app'
import {persistDisplay, removeDisplay, updateDisplay} from './BoardUtils.js'
import DisplayMatchEdit from './DisplayMatchEdit.vue'
import DisplayThemeEdit from './DisplayThemeEdit.vue'
import Json5 from 'json5'
import Themes from './Themes.json5'

const props = defineProps({
    display: Object,
})

const emit = defineEmits(['close'])

const page = reactive({
    applyName: null,
    basis: 'Standard',
    copyName: '',
    deleting: false,
    display: {},
    displays: [],
    existing: false,
    expressionType: 'Responsive',
    importFile: null,
    prior: null,
    saving: false,
    showCopy: false,
    showLibrary: false,
    showImport: false,
    themes: {},
    tab: 'match',
})

//  Component refs
const primary = ref(null)
const confirm = ref(null)

onBeforeMount(() => {
    page.display = clone(props.display)
    page.displays = State.app.fullDisplay.displays
    page.themes = Themes
})

async function save() {
    if (!page.display.name) {
        throw new Error('Must set display name')
    }
    updateDisplay(page.display)
    State.app.setNeedSave('display')
    emit('close')
}

async function copy() {
    page.prior = null
    page.showCopy = false
    page.display.name = page.copyName
    await save()
}

async function deleteDisplay() {
    if (!page.display.name) {
        return
    }
    if (await confirm.value.ask(`Do you want to delete the display "${page.display.name}"?`)) {
        page.deleting = true
        removeDisplay(page.display)
        await persistDisplay()
        Feedback.info('Display Deleted')
        page.deleting = false
    }
    emit('close')
}

async function applyTheme() {
    let theme = page.themes[page.applyName]
    page.display.theme = theme
    updateDisplay(page.display)
    State.app.setNeed('updateBoard')
    page.showLibrary = false
}

async function importTheme() {
    let theme
    try {
        let text = await page.importFile.text()
        theme = Json5.parse(text)
    } catch (err) {
        throw new Error(`Theme contains invalid JSON5`)
    } finally {
        page.showImport = false
    }
    page.display.theme = theme
    updateDisplay(page.display)
    State.app.setNeed('updateBoard')
    page.showImport = false
}

async function exportTheme() {
    let data = Json5.stringify(page.display.theme, null, 4)
    let blob = new Blob([data], {type: 'text/plain'})
    let link = document.createElement('a')
    link.download = 'theme.json5'
    if (window.webkitURL != null) {
        link.href = window.webkitURL.createObjectURL(blob)
    } else {
        link.href = window.URL.createObjectURL(blob)
        link.style.display = 'none'
        document.body.appendChild(link)
    }
    link.click()
}
</script>

<template>
    <vu-form
        class="display-edit"
        help="/doc/ui/display/display-edit.html"
        ref="primary"
        :data="page"
        :save="save"
        :title="`${page.display.name ? 'Modify' : 'Add'} Display`">
        <v-container fluid class="pa-0">
            <v-tabs v-model="page.tab" ref="tabs" class="page-tabs mb-5" fixed-tabs>
                <v-tab value="match">Matching</v-tab>
                <v-tab value="themes">Themes</v-tab>
            </v-tabs>

            <v-window v-model="page.tab" class="pt-4">
                <v-window-item value="match">
                    <DisplayMatchEdit v-model="page.display" />
                </v-window-item>
                <v-window-item value="themes">
                    <DisplayThemeEdit v-model="page.display" />
                </v-window-item>
            </v-window>
        </v-container>

        <div class="actions">
            <v-btn size="small" color="primary" type="submit" :loading="page.saving">Save</v-btn>
            <v-btn v-if="page.tab == 'match'" size="small" color="primary" @click="page.showCopy = true">Copy</v-btn>
            <v-btn size="small" color="none" @click="emit('close')">Cancel</v-btn>
            <v-btn
                v-if="page.display.name && page.tab == 'match' && page.displays.length > 1"
                size="small"
                color="error"
                :loading="page.deleting"
                @click="deleteDisplay">
                Delete
            </v-btn>
            <span v-if="page.tab == 'themes'">
                <v-btn size="small" color="primary" @click="exportTheme">Export</v-btn>
                <v-btn size="small" color="primary" @click="page.showImport = true">Import</v-btn>
                <v-btn size="small" color="primary" @click="page.showLibrary = true">Library</v-btn>
            </span>
        </div>

        <vu-confirm ref="confirm" />
    </vu-form>

    <v-dialog v-model="page.showCopy" width="550" content-class="display-dialog">
        <v-card>
            <v-card-title class="headline" primary-title>Copy Display</v-card-title>
            <v-card-text class="mt-4 px-4 pb-0"><label>Enter a name for the new display</label></v-card-text>
            <v-card-text class="px-4 pb-0">
                <v-text-field v-model="page.copyName" single-line label="Display Name" />
            </v-card-text>
            <v-card-actions class="save-as-actions px-4">
                <v-btn
                    ripple
                    color="primary"
                    variant="elevated"
                    :loading="page.saving"
                    type="submit"
                    @click="copy">
                    Save
                </v-btn>
                <v-btn ripple variant="elevated" @click="emit('close')">Cancel</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="page.showImport" content-class="display-dialog" width="500">
        <v-card>
            <v-card-title class="headline">Import Theme</v-card-title>
            <v-card-text class="import-theme">
                <vu-input
                    v-model="page.importFile"
                    class="mb-2"
                    clearable
                    label="Theme File"
                    name="importThemeFile"
                    placeholder="Theme.json5 file"
                    type="file"
                    :multiple="false" />
                <v-btn :disabled="!page.importFile" color="primary" @click="importTheme">Import</v-btn>
            </v-card-text>
        </v-card>
    </v-dialog>

    <v-dialog v-model="page.showLibrary" content-class="display-dialog" width="500">
        <v-card>
            <v-card-title class="headline">Apply Library Theme</v-card-title>
            <v-card-text class="import-theme">
                <vu-input
                    v-model="page.applyName"
                    class="mb-2"
                    label="Select Theme"
                    name="applyTheme"
                    placeholder="Select theme to apply"
                    type="select"
                    :items="Object.keys(page.themes)" />
                <v-btn class="mr-2" :disabled="!page.applyName" color="primary" @click="applyTheme">Save</v-btn>
                <v-btn @click="page.showLibrary = false">Cancel</v-btn>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<style lang="scss">
.display-dialog {
    .headline {
        background: rgb(var(--v-theme-primary));
        color: white;
        font-weight: bold;
        padding: 16px;
    }
}
.save-as-actions {
    margin: 16px 0 16px 0;
    flex: 0 0 100%;
    justify-content: flex-start !important;
    .v-btn {
        margin-right: 10px;
        @media (max-width: 640px) {
            margin-right: 6px;
            padding: 0 6px;
        }
    }
}
.display-edit {
    .page-tabs {
        background: rgb(var(--v-theme-background));
        border: solid 1px rgb(var(--v-theme-border));
        .v-tab {
            border-right: solid 1px rgb(var(--v-theme-border));
        }
        .v-tab--selected {
            background: rgb(var(--v-theme-accent));
            color: white;
        }
        .v-btn {
            padding: 0 8px;
            font-size: 11px !important;
        }
    }
}
.import-theme {
    background-color: rgb(var(--v-theme-surface-lighten-1));
    padding: 24px !important;
}
</style>
